import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { rtdb, auth } from "../firebase";
import { ref, onValue, query, orderByChild, limitToLast } from "firebase/database";
import Header from "../components/Header";

function History() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, form1-form31
  const [trackingQuery, setTrackingQuery] = useState("");

  useEffect(() => {
    // Check authentication
    if (!auth.currentUser) {
      navigate("/login");
      return;
    }

    // Subscribe to download history from Firebase RTDB
    const historyRef = ref(rtdb, "/downloadHistory");
    const historyQuery = query(historyRef, orderByChild("timestamp"), limitToLast(100));

    const unsubscribe = onValue(
      historyQuery,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          // Convert object to array and sort by timestamp descending
          const historyArray = Object.keys(data)
            .map((key) => ({
              id: key,
              ...data[key],
            }))
            .sort((a, b) => b.timestamp - a.timestamp);
          setHistory(historyArray);
        } else {
          setHistory([]);
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching history:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [navigate]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const getFormColor = (formName) => {
    if (formName === "FORM1.docx") return "from-emerald-400 to-teal-500";
    if (formName === "FORM2.docx") return "from-sky-400 to-cyan-500";
    if (formName === "FORM3.docx") return "from-amber-400 to-orange-500";
    if (formName === "FORM4.docx") return "from-rose-400 to-pink-500";
    if (formName === "FORM5.docx") return "from-purple-400 to-pink-500";
    if (formName === "FORM6.docx") return "from-indigo-400 to-purple-500";
    if (formName === "FORM7A.docx") return "from-lime-400 to-green-500";
    if (formName === "FORM7.docx") return "from-green-400 to-emerald-500";
    if (formName === "FORM8A.docx") return "from-yellow-400 to-amber-500";
    if (formName === "FORM8.docx") return "from-orange-400 to-red-500";
    if (formName === "FORM9.docx") return "from-fuchsia-400 to-purple-500";
    if (formName === "FORM10.docx") return "from-violet-400 to-indigo-500";
    if (formName === "FORM11.docx") return "from-slate-400 to-gray-500";
    if (formName === "FORM12.docx") return "from-red-400 to-rose-500";
    if (formName === "FORM13.docx") return "from-orange-400 to-yellow-500";
    if (formName === "FORM14.docx") return "from-pink-400 to-fuchsia-500";
    if (formName === "FORM15.docx") return "from-emerald-400 to-green-500";
    if (formName === "FORM28.docx") return "from-teal-400 to-cyan-500";
    return "from-blue-400 to-indigo-500";
  };

  const getFormIcon = (formName) => {
    if (formName === "FORM1.docx") return "📄";
    if (formName === "FORM2.docx") return "📋";
    if (formName === "FORM3.docx") return "📝";
    if (formName === "FORM4.docx") return "📑";
    if (formName === "FORM5.docx") return "📃";
    if (formName === "FORM6.docx") return "📄";
    if (formName === "FORM7.docx") return "📋";
    if (formName === "FORM7A.docx") return "📋";
    if (formName === "FORM8.docx") return "📝";
    if (formName === "FORM8A.docx") return "📝";
    if (formName === "FORM9.docx") return "📑";
    if (formName === "FORM10.docx") return "📃";
    if (formName === "FORM11.docx") return "📋";
    if (formName === "FORM12.docx") return "🔖";
    if (formName === "FORM13.docx") return "✏️";
    if (formName === "FORM14.docx") return "⚠️";
    if (formName === "FORM15.docx") return "🔄";
    if (formName === "FORM28.docx") return "🔍";
    return "📄";
  };

  const formatName = (name) => {
    if (!name || typeof name !== "string") return name;
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const filteredHistory = history.filter((item) => {
    const formName = item.formName || "";
    const matchesForm =
      filter === "all" ||
      (filter === "form1" && formName === "FORM1.docx") ||
      (filter === "form2" && formName === "FORM2.docx") ||
      (filter === "form3" && formName === "FORM3.docx") ||
      (filter === "form4" && formName === "FORM4.docx") ||
      (filter === "form5" && formName === "FORM5.docx") ||
      (filter === "form6" && formName === "FORM6.docx") ||
      (filter === "form7a" && formName === "FORM7A.docx") ||
      (filter === "form7" && formName === "FORM7.docx") ||
      (filter === "form8a" && formName === "FORM8A.docx") ||
      (filter === "form8" && formName === "FORM8.docx") ||
      (filter === "form9" && formName === "FORM9.docx") ||
      (filter === "form10" && formName === "FORM10.docx") ||
      (filter === "form11" && formName === "FORM11.docx") ||
      (filter === "form12" && formName === "FORM12.docx") ||
      (filter === "form13" && formName === "FORM13.docx") ||
      (filter === "form14" && formName === "FORM14.docx") ||
      (filter === "form15" && formName === "FORM15.docx") ||
      (filter === "form16" && formName === "FORM16.docx") ||
      (filter === "form17" && formName === "FORM17.docx") ||
      (filter === "form18" && formName === "FORM18.docx") ||
      (filter === "form18a" && formName === "FORM18A.docx") ||
      (filter === "form19" && formName === "FORM19.docx") ||
      (filter === "form20" && formName === "FORM20.docx") ||
      (filter === "form21" && formName === "FORM21.docx") ||
      (filter === "form22" && formName === "FORM22.docx") ||
      (filter === "form23" && formName === "FORM23.docx") ||
      (filter === "form24" && formName === "FORM24.docx") ||
      (filter === "form25" && formName === "FORM25.docx") ||
      (filter === "form26" && formName === "FORM26.docx") ||
      (filter === "form27" && formName === "FORM27.docx") ||
      (filter === "form28" && formName === "FORM28.docx") ||
      (filter === "form29" && formName === "FORM29.docx") ||
      (filter === "form30" && formName === "FORM30.docx") ||
      (filter === "form31" && formName === "FORM31.docx");

    const query = trackingQuery.trim().toLowerCase();
    const tn = (item.trackingNumber || "").toLowerCase();
    const matchesTracking = query === "" || tn.includes(query);

    return matchesForm && matchesTracking;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex flex-col">
      <Header />
      
      <div className="relative overflow-hidden flex-1">
        {/* Animated Background Elements */}
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-blue-300 opacity-10 rounded-full filter blur-3xl animate-float-slow z-0" />
        <div className="absolute top-1/3 -right-20 w-[500px] h-[500px] bg-purple-200 opacity-10 rounded-full filter blur-3xl animate-float-medium z-0" />
        <div className="absolute bottom-20 left-1/4 w-[400px] h-[400px] bg-teal-200 opacity-10 rounded-full filter blur-3xl animate-float-fast z-0" />

        {/* Main Content */}
        <main className="relative z-10 w-full max-w-6xl mx-auto px-4 py-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Header Section */}
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-400 mb-4">
                Download History
              </h1>
              <p className="text-lg text-gray-700">
                Track all form downloads with creator details and timestamps
              </p>
            </div>

            {/* Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              <button
                onClick={() => setFilter("all")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "all"
                    ? "bg-gradient-to-r from-blue-600 to-blue-400 text-white shadow-lg"
                    : "bg-white text-blue-600 border-2 border-blue-100 hover:border-blue-300"
                }`}
              >
                All Forms
              </button>
              <button
                onClick={() => setFilter("form1")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form1"
                    ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg"
                    : "bg-white text-emerald-600 border-2 border-emerald-100 hover:border-emerald-300"
                }`}
              >
                Form 1
              </button>
              <button
                onClick={() => setFilter("form2")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form2"
                    ? "bg-gradient-to-r from-sky-500 to-cyan-500 text-white shadow-lg"
                    : "bg-white text-sky-600 border-2 border-sky-100 hover:border-sky-300"
                }`}
              >
                Form 2
              </button>
              <button
                onClick={() => setFilter("form3")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form3"
                    ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg"
                    : "bg-white text-amber-600 border-2 border-amber-100 hover:border-amber-300"
                }`}
              >
                Form 3
              </button>
              <button
                onClick={() => setFilter("form4")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form4"
                    ? "bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg"
                    : "bg-white text-rose-600 border-2 border-rose-100 hover:border-rose-300"
                }`}
              >
                Form 4
              </button>
              <button
                onClick={() => setFilter("form5")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form5"
                    ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg"
                    : "bg-white text-purple-600 border-2 border-purple-100 hover:border-purple-300"
                }`}
              >
                Form 5
              </button>
              <button
                onClick={() => setFilter("form6")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form6"
                    ? "bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg"
                    : "bg-white text-indigo-600 border-2 border-indigo-100 hover:border-indigo-300"
                }`}
              >
                Form 6
              </button>
              <button
                onClick={() => setFilter("form7")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form7"
                    ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg"
                    : "bg-white text-green-600 border-2 border-green-100 hover:border-green-300"
                }`}
              >
                Form 7
              </button>
              <button
                onClick={() => setFilter("form7a")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form7a"
                    ? "bg-gradient-to-r from-lime-500 to-green-500 text-white shadow-lg"
                    : "bg-white text-lime-600 border-2 border-lime-100 hover:border-lime-300"
                }`}
              >
                Form 7A
              </button>
              <button
                onClick={() => setFilter("form8")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form8"
                    ? "bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg"
                    : "bg-white text-orange-600 border-2 border-orange-100 hover:border-orange-300"
                }`}
              >
                Form 8
              </button>
              <button
                onClick={() => setFilter("form8a")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form8a"
                    ? "bg-gradient-to-r from-yellow-500 to-amber-500 text-white shadow-lg"
                    : "bg-white text-yellow-600 border-2 border-yellow-100 hover:border-yellow-300"
                }`}
              >
                Form 8A
              </button>
              <button
                onClick={() => setFilter("form9")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form9"
                    ? "bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white shadow-lg"
                    : "bg-white text-fuchsia-600 border-2 border-fuchsia-100 hover:border-fuchsia-300"
                }`}
              >
                Form 9
              </button>
              <button
                onClick={() => setFilter("form10")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form10"
                    ? "bg-gradient-to-r from-violet-500 to-indigo-500 text-white shadow-lg"
                    : "bg-white text-violet-600 border-2 border-violet-100 hover:border-violet-300"
                }`}
              >
                Form 10
              </button>
              <button
                onClick={() => setFilter("form11")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form11"
                    ? "bg-gradient-to-r from-slate-500 to-gray-500 text-white shadow-lg"
                    : "bg-white text-slate-600 border-2 border-slate-100 hover:border-slate-300"
                }`}
              >
                Form 11
              </button>
              <button
                onClick={() => setFilter("form12")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form12"
                    ? "bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-lg"
                    : "bg-white text-red-600 border-2 border-red-100 hover:border-red-300"
                }`}
              >
                Form 12
              </button>
              <button
                onClick={() => setFilter("form13")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form13"
                    ? "bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-lg"
                    : "bg-white text-orange-600 border-2 border-orange-100 hover:border-orange-300"
                }`}
              >
                Form 13
              </button>
              <button
                onClick={() => setFilter("form14")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form14"
                    ? "bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white shadow-lg"
                    : "bg-white text-pink-600 border-2 border-pink-100 hover:border-pink-300"
                }`}
              >
                Form 14
              </button>
              <button
                onClick={() => setFilter("form15")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form15"
                    ? "bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg"
                    : "bg-white text-emerald-600 border-2 border-emerald-100 hover:border-emerald-300"
                }`}
              >
                Form 15
              </button>
              <button
                onClick={() => setFilter("form16")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form16"
                    ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg"
                    : "bg-white text-purple-600 border-2 border-purple-100 hover:border-purple-300"
                }`}
              >
                Form 16
              </button>
              <button
                onClick={() => setFilter("form17")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form17"
                    ? "bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg"
                    : "bg-white text-orange-600 border-2 border-orange-100 hover:border-orange-300"
                }`}
              >
                Form 17
              </button>
              <button
                onClick={() => setFilter("form18")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form18"
                    ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg"
                    : "bg-white text-blue-600 border-2 border-blue-100 hover:border-blue-300"
                }`}
              >
                Form 18
              </button>
              <button
                onClick={() => setFilter("form18a")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form18a"
                    ? "bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-lg"
                    : "bg-white text-teal-600 border-2 border-teal-100 hover:border-teal-300"
                }`}
              >
                Form 18A
              </button>
              <button
                onClick={() => setFilter("form19")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form19"
                    ? "bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg"
                    : "bg-white text-rose-600 border-2 border-rose-100 hover:border-rose-300"
                }`}
              >
                Form 19
              </button>
              <button
                onClick={() => setFilter("form20")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form20"
                    ? "bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-lg"
                    : "bg-white text-amber-600 border-2 border-amber-100 hover:border-amber-300"
                }`}
              >
                Form 20
              </button>
              <button
                onClick={() => setFilter("form21")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form21"
                    ? "bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg"
                    : "bg-white text-indigo-600 border-2 border-indigo-100 hover:border-indigo-300"
                }`}
              >
                Form 21
              </button>
              <button
                onClick={() => setFilter("form22")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form22"
                    ? "bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg"
                    : "bg-white text-violet-600 border-2 border-violet-100 hover:border-violet-300"
                }`}
              >
                Form 22
              </button>
              <button
                onClick={() => setFilter("form23")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form23"
                    ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg"
                    : "bg-white text-pink-600 border-2 border-pink-100 hover:border-pink-300"
                }`}
              >
                Form 23
              </button>
              <button
                onClick={() => setFilter("form24")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form24"
                    ? "bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg"
                    : "bg-white text-red-600 border-2 border-red-100 hover:border-red-300"
                }`}
              >
                Form 24
              </button>
              <button
                onClick={() => setFilter("form25")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form25"
                    ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg"
                    : "bg-white text-orange-600 border-2 border-orange-100 hover:border-orange-300"
                }`}
              >
                Form 25
              </button>
              <button
                onClick={() => setFilter("form26")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form26"
                    ? "bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg"
                    : "bg-white text-teal-600 border-2 border-teal-100 hover:border-teal-300"
                }`}
              >
                Form 26
              </button>
              <button
                onClick={() => setFilter("form27")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form27"
                    ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg"
                    : "bg-white text-cyan-600 border-2 border-cyan-100 hover:border-cyan-300"
                }`}
              >
                Form 27
              </button>
              <button
                onClick={() => setFilter("form28")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form28"
                    ? "bg-gradient-to-r from-teal-500 to-green-500 text-white shadow-lg"
                    : "bg-white text-teal-600 border-2 border-teal-100 hover:border-teal-300"
                }`}
              >
                Form 28
              </button>
              <button
                onClick={() => setFilter("form29")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form29"
                    ? "bg-gradient-to-r from-lime-500 to-green-500 text-white shadow-lg"
                    : "bg-white text-lime-600 border-2 border-lime-100 hover:border-lime-300"
                }`}
              >
                Form 29
              </button>
              <button
                onClick={() => setFilter("form30")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form30"
                    ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg"
                    : "bg-white text-amber-600 border-2 border-amber-100 hover:border-amber-300"
                }`}
              >
                Form 30
              </button>
              <button
                onClick={() => setFilter("form31")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form31"
                    ? "bg-gradient-to-r from-rose-500 to-red-500 text-white shadow-lg"
                    : "bg-white text-rose-600 border-2 border-rose-100 hover:border-rose-300"
                }`}
              >
                Form 31
              </button>
              <button
                onClick={() => setFilter("form28")}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
                  filter === "form28"
                    ? "bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg"
                    : "bg-white text-teal-600 border-2 border-teal-100 hover:border-teal-300"
                }`}
              >
                Form 28
              </button>
            </div>

            {/* Tracking Number Search */}
            <div className="flex justify-center mb-8">
              <div className="flex flex-col items-start w-full max-w-md gap-2">
                <label className="text-sm font-semibold text-gray-700">Tracking Number</label>
                <input
                  type="text"
                  className="border-2 border-blue-100 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                  placeholder="Search by tracking number..."
                  value={trackingQuery}
                  onChange={(e) => setTrackingQuery(e.target.value)}
                />
              </div>
            </div>

            {/* History List */}
            <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-blue-100 overflow-hidden">
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredHistory.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-2xl text-gray-500 mb-2">📭</p>
                  <p className="text-lg text-gray-600">No download history found</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Start generating forms to see your history here
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-blue-600 to-blue-400 text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-semibold">#</th>
                        <th className="px-6 py-4 text-left font-semibold">Applicant Name</th>
                        <th className="px-6 py-4 text-left font-semibold">Creator Name</th>
                        <th className="px-6 py-4 text-left font-semibold">Patent ID</th>
                        <th className="px-6 py-4 text-left font-semibold">Tracking Number</th>
                        <th className="px-6 py-4 text-left font-semibold">Form</th>
                        <th className="px-6 py-4 text-left font-semibold">Date</th>
                        <th className="px-6 py-4 text-left font-semibold">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHistory.map((item, index) => (
                        <motion.tr
                          key={item.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05, duration: 0.3 }}
                          className="border-b border-blue-50 hover:bg-blue-50/50 transition-colors"
                        >
                          <td className="px-6 py-4 text-gray-700 font-medium">
                            {filteredHistory.length - index}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold">
                                {item.applicantName?.charAt(0).toUpperCase() || "?"}
                              </div>
                              <span className="font-semibold text-gray-800">
                                {item.applicantName || "Unknown"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-semibold text-gray-800">
                              {formatName(item.creatorName) || "Unknown"}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg font-medium text-sm">
                              {item.patentId || "N/A"}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-gray-50 text-gray-700 rounded-lg font-medium text-sm">
                              {item.trackingNumber || "N/A"}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{getFormIcon(item.formName)}</span>
                              <span
                                className={`px-3 py-1 rounded-full text-white font-semibold text-sm bg-gradient-to-r ${getFormColor(
                                  item.formName
                                )}`}
                              >
                                {item.formName || "Unknown"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-700 font-medium">
                            {formatDate(item.timestamp)}
                          </td>
                          <td className="px-6 py-4 text-gray-600">
                            {formatTime(item.timestamp)}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Stats Section */}
            {!loading && filteredHistory.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg p-6 border border-blue-100">
                  <p className="text-gray-600 mb-1">Total Downloads</p>
                  <p className="text-3xl font-bold text-blue-600">{filteredHistory.length}</p>
                </div>
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg p-6 border border-blue-100">
                  <p className="text-gray-600 mb-1">Unique Creators</p>
                  <p className="text-3xl font-bold text-emerald-600">
                    {new Set(filteredHistory.map((h) => h.creatorName)).size}
                  </p>
                </div>
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg p-6 border border-blue-100">
                  <p className="text-gray-600 mb-1">Most Recent</p>
                  <p className="text-lg font-bold text-purple-600">
                    {formatDate(filteredHistory[0]?.timestamp)}
                  </p>
                </div>
              </motion.div>
            )}
          </motion.div>
        </main>
      </div>

      <style jsx global>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
        @keyframes float-medium {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-15px) translateX(-15px); }
        }
        @keyframes float-fast {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-10px) translateX(5px); }
        }
        .animate-float-slow { animation: float-slow 10s ease-in-out infinite; }
        .animate-float-medium { animation: float-medium 8s ease-in-out infinite; }
        .animate-float-fast { animation: float-fast 6s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

export default History;
